/**
 * Migration Service
 *
 * Handles one-time data migrations between app versions.
 * Migration state is tracked in localStorage to ensure each version only runs once.
 *
 * For future migrations: increment CURRENT_MIGRATION_VERSION and add new logic
 * to checkAndRunMigrations().
 *
 * Version history:
 * - v1: Migrated productCategories and timeslots from RxDB to Dexie (now simplified to force config reload)
 */

import { LocalStorageKeys } from "@/services/storage.service";
import { LogService, LogLevel } from "@/services/logging";
import AppModule from "@/store/modules/app";
import AuthModule from "@/store/modules/auth";
import POSConfigModule from "@/store/modules/pos-config";
import { getUserAccessToken } from "@/utils/helpers/auth";
import { redirectToLoginPage } from "@/utils/helpers/router.helper";

interface IMigrationState {
  version: number;
  migratedAt: string;
}

const CURRENT_MIGRATION_VERSION = 1;

/**
 * Check if migration has already been completed
 */
export function isMigrationNeeded(): boolean {
  try {
    const state = localStorage.getItem(LocalStorageKeys.DataMigration);
    if (!state) {
      LogService.info("Migration: No migration state found, migration needed");
      return true;
    }

    const parsedState: IMigrationState = JSON.parse(state);
    if (parsedState.version < CURRENT_MIGRATION_VERSION) {
      LogService.info(
        `Migration: Old migration version ${parsedState.version} found, migration needed`,
        { parsedState }
      );
      return true;
    }
  } catch (error) {
    LogService.info(
      "Migration: Failed to parse migration state, migration needed",
      { error },
      LogLevel.Info
    );
    return true;
  }
  return false;
}

/**
 * Mark migration as complete
 */
export function setMigrationComplete(): void {
  const state: IMigrationState = {
    version: CURRENT_MIGRATION_VERSION,
    migratedAt: new Date().toISOString(),
  };
  localStorage.setItem(LocalStorageKeys.DataMigration, JSON.stringify(state));
}

/**
 * Checks migration state and runs necessary migration steps.
 *
 * v1: Forces a POS config reload by clearing the selected config and logging out.
 * The user will re-login and select a POS config, which populates Dexie through the normal flow.
 */
export async function checkAndRunMigrations(): Promise<void> {
  if (!isMigrationNeeded()) {
    return;
  }

  // If no user is logged in, skip the migration.
  // Clear the selected POS config so the user will be forced to select one upon login.
  const accessToken = getUserAccessToken();
  if (!accessToken) {
    LogService.info("Migration: User is not logged in, clearing POS config");
    POSConfigModule.SET_SELECTED_CONFIG(null);
    setMigrationComplete();
    return;
  }

  LogService.info(
    "Migration: Forcing config reload by clearing POS config and logging out"
  );

  try {
    AppModule.setIsRefreshing(true);
    POSConfigModule.SET_SELECTED_CONFIG(null);
    AuthModule.UserLogOut();
    redirectToLoginPage();
  } catch (error) {
    LogService.info(
      "Migration: Migration failed with error",
      { error },
      LogLevel.Error
    );
  } finally {
    setMigrationComplete();
    AppModule.setIsRefreshing(false);
  }
}
